import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Drawer, Button, Pagination, AgGridTable } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FiDownload } from 'react-icons/fi';
import apiAxios from 'helpers/apiAxios';
import { ExportToExcel } from 'helpers/utils';

const ENDPOINT  = '/crm/dashboard/contracts/get_unreleased_data';
const PAGE_SIZE = 30;

// Items pending release for this many days or more are flagged as needing attention.
const STUCK_DAYS = 14;

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (iso) => {
  if (!iso) return '—';
  const p = String(iso).split('-');
  if (p.length !== 3) return iso;
  return `${p[2].padStart(2, '0')}-${MONTHS_SHORT[parseInt(p[1], 10) - 1]}-${p[0]}`;
};
const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MT`;

const UnreleasedListPanel = ({ open, baseParams, onClose }) => {
  const [records,      setRecords]      = useState([]);
  const [totals,       setTotals]       = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [exporting,    setExporting]    = useState(false);
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchRecords = useCallback(async (pg) => {
    setLoading(true);
    try {
      const res = await apiAxios.get(ENDPOINT, {
        params: { ...baseParams, pagination: { page: pg, per_page: PAGE_SIZE } }
      });
      setRecords(res.data?.data || []);
      setTotalPages(res.data?.pagination?.total_pages || 1);
      setTotalRecords(res.data?.pagination?.total_records || 0);
      setTotals(res.data?.totals || null);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [baseParams]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    fetchRecords(1);
  }, [open, fetchRecords]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const allPages = [];
      let p = 1;
      let total = 1;
      do {
        const res = await apiAxios.get(ENDPOINT, {
          params: { ...baseParams, pagination: { page: p, per_page: 200 } }
        });
        allPages.push(...(res.data?.data || []));
        total = res.data?.pagination?.total_pages || 1;
        p += 1;
      } while (p <= total);

      const exportRows = allPages.map(r => ({
        'Type':           r.type || '',
        'Number':         r.number || '',
        'Contract #':     r.contract_number || '',
        'J Number':       r.j_number || r.q_number || '',
        'Project':        r.project_name || '',
        'Customer':       r.customer_name || '',
        'Value':          r.value || 0,
        'Weight (MT)':    r.weight || 0,
        'Signed':         r.signed ? 'Yes' : 'No',
        'Signed By':      r.signed_by || '',
        'Pending (days)': r.age_days ?? '',
        'Created':        r.created_at || '',
        'Owner':          r.created_by || '',
        'Sales Incharge': r.sales_incharge || '',
        'Sales Unit':     r.unit_name || '',
        'Region':         r.region || ''
      }));

      ExportToExcel(exportRows, `Unreleased_Contracts_Addendums_${new Date().toISOString().slice(0, 10)}`);
    } catch {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const columnDefs = useMemo(() => [
    {
      headerName: 'Type',
      field:      'type',
      width:      110,
      pinned:     'left',
      cellRenderer: (p) => (
        <span
          className={`tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold ${
            p.value === 'Addendum' ? 'tw-bg-orange-100 tw-text-orange-700' : 'tw-bg-blue-100 tw-text-blue-700'
          }`}
        >
          {p.value}
        </span>
      )
    },
    { headerName: 'Number',     field: 'number',          width: 140 },
    { headerName: 'Contract #', field: 'contract_number', width: 120, cellStyle: { fontSize: '11px', color: '#6B7280' } },
    {
      headerName: 'J Number',
      field:      'j_number',
      width:      120,
      cellRenderer: (p) => {
        const num = p.data?.j_number || p.data?.q_number || '—';
        return p.data?.deal_id
          ? <Link to={`/crm/projects/${p.data.deal_id}`} target="_blank" rel="noreferrer" className="tw-font-mono tw-font-bold tw-text-blue-700 hover:tw-underline">{num}</Link>
          : num;
      }
    },
    { headerName: 'Project',  field: 'project_name',  flex: 2, minWidth: 150 },
    { headerName: 'Customer', field: 'customer_name', flex: 1, minWidth: 130 },
    {
      headerName:    'Value',
      field:         'value',
      width:         110,
      type:          'numericColumn',
      valueFormatter: p => fmtM(p.value),
      cellStyle:     { textAlign: 'right', fontWeight: 600, color: '#1D4ED8' }
    },
    {
      headerName:    'Weight (MT)',
      field:         'weight',
      width:         110,
      type:          'numericColumn',
      valueFormatter: p => `${Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MT`,
      cellStyle:     { textAlign: 'right' }
    },
    {
      headerName: 'Signed',
      field:      'signed',
      width:      90,
      cellRenderer: p => p.value
        ? <span className="tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-medium tw-bg-green-100 tw-text-green-700">Yes</span>
        : <span className="tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-medium tw-bg-red-100 tw-text-red-700">No</span>
    },
    {
      headerName: 'Pending',
      field:      'age_days',
      width:      95,
      type:       'numericColumn',
      cellRenderer: (p) => {
        if (p.value === null || p.value === undefined) return '—';
        const stuck = p.value >= STUCK_DAYS;
        return (
          <span className={`tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold ${stuck ? 'tw-bg-red-100 tw-text-red-700' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
            {p.value}d
          </span>
        );
      },
      cellStyle: { textAlign: 'center' }
    },
    { headerName: 'Created',  field: 'created_at',     width: 120, valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#6B7280', fontSize: '11px' } },
    { headerName: 'Owner',    field: 'created_by',     width: 110, cellStyle: { fontSize: '11px' } },
    { headerName: 'Incharge', field: 'sales_incharge', width: 110, cellStyle: { fontSize: '11px', color: '#6B7280' } }
  ], []);

  const drawerTitle = (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-pr-2">
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-font-bold tw-text-slate-800">Unreleased Contracts &amp; Addendums</span>
        {!loading && totalRecords > 0 && (
          <span className="tw-text-xs tw-text-orange-700 tw-bg-orange-100 tw-px-2 tw-py-0.5 tw-rounded-xl tw-font-semibold">
            {totalRecords.toLocaleString()} records
          </span>
        )}
      </div>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={FiDownload}
        loading={exporting}
        disabled={loading || totalRecords === 0}
        onClick={handleExport}
        className="tw-border-emerald-300 tw-bg-emerald-50 tw-text-emerald-700 hover:tw-bg-emerald-100"
      >
        {exporting ? 'Exporting…' : 'Export Excel'}
      </Button>
    </div>
  );

  const summaryBar = totals ? (
    <div className="tw-flex tw-flex-wrap tw-gap-3 tw-px-1 tw-py-2 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded">
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <span className="tw-text-xs tw-text-gray-500">Contracts:</span>
        <span className="tw-text-xs tw-font-bold tw-text-blue-700">{totals.unreleased_contracts}</span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <span className="tw-text-xs tw-text-gray-500">Addendums:</span>
        <span className="tw-text-xs tw-font-bold tw-text-amber-600">{totals.unreleased_addendums}</span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <span className="tw-text-xs tw-text-gray-500">Value:</span>
        <span className="tw-text-xs tw-font-bold tw-text-emerald-700">{fmtM(totals.unreleased_value)}</span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <span className="tw-text-xs tw-text-gray-500">Weight:</span>
        <span className="tw-text-xs tw-font-bold tw-text-gray-700">{fmtW(totals.unreleased_weight)}</span>
      </div>
    </div>
  ) : null;

  const footer = (summaryBar || totalPages > 1) ? (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {summaryBar}
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalRecords}
          perPage={PAGE_SIZE}
          onPageChange={(pg) => { setPage(pg); fetchRecords(pg); }}
          disabled={loading}
        />
      )}
    </div>
  ) : null;

  return (
    <Drawer show={open} onHide={onClose} title={drawerTitle} footer={footer} width="980px" minWidth="340px">
      <p className="tw-text-xs tw-text-gray-500 tw-mb-2">
        Still pending release, oldest first. Rows pending {STUCK_DAYS}+ days are flagged red — check <b>Signed</b> and <b>Owner</b> to find why they are stuck.
      </p>
      <AgGridTable
        rowData={records}
        columnDefs={columnDefs}
        loading={loading}
        pagination={false}
        filter={false}
        sort={false}
        rowHeight={36}
        headerHeight={38}
        fontSize="12px"
        height="max(320px, calc(100dvh - 210px))"
        enableCellTextSelection
      />
    </Drawer>
  );
};

UnreleasedListPanel.propTypes = {
  open:       PropTypes.bool.isRequired,
  baseParams: PropTypes.object.isRequired,
  onClose:    PropTypes.func.isRequired
};

export default UnreleasedListPanel;
